import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { CheckCircle, FileText, Pen, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function formatMoney(val) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(val || 0);
}

export default function SignaturePubliquePage() {
  const { docId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [doc, setDoc] = useState(null);
  const [dejaSigne, setDejaSigne] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [nom, setNom] = useState('');
  const [fonction, setFonction] = useState('');
  const [commentaire, setCommentaire] = useState('');
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/gestion-commerciale/signature/${docId}?token=${token}`);
        if (!res.ok) { setError('Document introuvable ou lien expiré'); return; }
        const data = await res.json();
        setDoc(data.document);
        setDejaSigne(data.deja_signe);
      } catch { setError('Erreur de connexion'); }
      finally { setLoading(false); }
    })();
  }, [docId, token]);

  const handleSign = async () => {
    if (!nom.trim()) return;
    setSigning(true);
    try {
      const res = await fetch(`${API}/api/gestion-commerciale/signature/${docId}?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signataire_nom: nom, signataire_fonction: fonction, commentaire })
      });
      if (res.ok) { setSigned(true); }
      else { const e = await res.json(); setError(e.detail || 'Erreur'); }
    } catch { setError('Erreur réseau'); }
    finally { setSigning(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
      <div className="text-zinc-500">Chargement...</div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <div className="text-center">
        <AlertTriangle className="mx-auto text-red-400 mb-3" size={48} />
        <div className="text-red-400 font-medium">{error}</div>
      </div>
    </div>
  );

  if (signed || dejaSigne) return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4" data-testid="signature-success">
      <div className="text-center max-w-sm">
        <CheckCircle className="mx-auto text-emerald-400 mb-4" size={64} />
        <h1 className="text-xl font-bold text-zinc-100 mb-2">Document signé</h1>
        <p className="text-zinc-400 text-sm">
          {dejaSigne ? `Signé par ${doc?.signature?.signataire_nom}` : `Merci ${nom}, votre signature a été enregistrée.`}
        </p>
        <div className="mt-4 p-3 bg-zinc-900 rounded-lg">
          <div className="text-sm text-zinc-300">{doc?.numero}</div>
          <div className="text-lg font-bold text-emerald-400">{formatMoney(doc?.total_ttc)}</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-950 p-4 max-w-lg mx-auto" data-testid="signature-page">
      {/* En-tête */}
      <div className="text-center mb-6 pt-6">
        <FileText className="mx-auto text-blue-400 mb-2" size={36} />
        <h1 className="text-lg font-bold text-zinc-100">Réception de chantier</h1>
        <div className="text-sm text-zinc-500">{doc?.numero}</div>
      </div>

      {/* Résumé document */}
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-5 mb-5">
        <div className="text-xs text-zinc-500 uppercase tracking-wide mb-2">Client</div>
        <div className="text-zinc-200 font-medium mb-4">{doc?.client?.nom}</div>

        <div className="text-xs text-zinc-500 uppercase tracking-wide mb-2">Prestations</div>
        <div className="space-y-2 mb-4">
          {doc?.lignes?.map((l, i) => (
            <div key={i} className="flex justify-between text-sm">
              <span className="text-zinc-400">{l.description} ({l.quantite} {l.unite})</span>
              <span className="text-zinc-300">{formatMoney(l.montant_ht)}</span>
            </div>
          ))}
        </div>

        <div className="pt-3 border-t border-zinc-800 text-right">
          <div className="text-sm text-zinc-500">Total HT : {formatMoney(doc?.total_ht)}</div>
          <div className="text-lg font-bold text-zinc-100">{formatMoney(doc?.total_ttc)} TTC</div>
        </div>
      </div>

      {/* Formulaire signature */}
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-zinc-300 flex items-center gap-2 mb-4">
          <Pen size={16} /> Signer la réception
        </h2>
        <div className="space-y-3">
          <Input placeholder="Votre nom *" value={nom} onChange={e => setNom(e.target.value)}
            className="bg-zinc-800/50 border-zinc-700" data-testid="sign-nom" />
          <Input placeholder="Fonction (optionnel)" value={fonction} onChange={e => setFonction(e.target.value)}
            className="bg-zinc-800/50 border-zinc-700" />
          <textarea placeholder="Commentaire (optionnel)" value={commentaire} onChange={e => setCommentaire(e.target.value)}
            className="w-full bg-zinc-800/50 border border-zinc-700 rounded-lg p-3 text-sm text-zinc-300 resize-none h-20" />
          <Button onClick={handleSign} disabled={signing || !nom.trim()}
            className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-base" data-testid="sign-submit-btn">
            {signing ? 'Signature...' : 'Signer la réception'}
          </Button>
        </div>
        <p className="text-xs text-zinc-600 mt-3 text-center">
          En signant, vous confirmez la bonne réception des travaux décrits ci-dessus.
        </p>
      </div>
    </div>
  );
}
